import { Document, Page, Text, View } from '@react-pdf/renderer';
import { DemandaUsoInsumo } from '../../@types';
import styles from '../../styles/features/pending-aproves/pending-demanda-uso-insumo-report-pdf.styles';
import {
  OTHER_COLS_COUNT,
  PRODUTO_COL_MEDIUM_PCT,
  PRODUTO_COL_NARROW_PCT,
  PRODUTO_COL_WIDE_PCT,
  PRODUTO_DESC_LONG,
  PRODUTO_DESC_MEDIUM,
  TABLE_WIDTH_PCT,
} from './demanda-uso-insumo-report.constants';

interface DemandaUsoInsumoReportDocProps {
  data: DemandaUsoInsumo[];
  startDate: string;
  endDate: string;
  generatedAt: string;
  pageUrl: string;
}

const formatQuantidade = (value: number, unit: string) => {
  const [intPart, decPart] = value.toFixed(4).split('.');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${intFormatted},${decPart} ${unit.trim()}`;
};

const getProdutoColumnStyle = (descricoes: string[]) => {
  const maxLength = Math.max(
    ...descricoes.map((descricao) => descricao?.length ?? 0),
    0,
  );

  if (maxLength >= PRODUTO_DESC_LONG) {
    return PRODUTO_COL_WIDE_PCT;
  }

  if (maxLength >= PRODUTO_DESC_MEDIUM) {
    return PRODUTO_COL_MEDIUM_PCT;
  }

  return PRODUTO_COL_NARROW_PCT;
};

const DemandaUsoInsumoReportDoc = ({
  data,
  startDate,
  endDate,
  generatedAt,
  pageUrl,
}: DemandaUsoInsumoReportDocProps) => (
  <Document>
    <Page style={styles.page}>
      <View style={styles.pageHeader} fixed>
        <Text style={styles.headerSide}>{generatedAt}</Text>
        <Text style={styles.headerCenter}>{pageUrl}</Text>
        <Text style={styles.headerSide}> </Text>
      </View>

      {data.map((item) => {
        const produtoColumnWidth = getProdutoColumnStyle(
          item.insumos.map((insumo) => insumo.descricao),
        );
        const otherColumnWidthValue =
          (TABLE_WIDTH_PCT - produtoColumnWidth) / OTHER_COLS_COUNT;
        const otherColumnWidth = `${otherColumnWidthValue}%`;
        const produtoColumnStyle = {
          width: `${produtoColumnWidth}%`,
          maxWidth: `${produtoColumnWidth}%`,
          flexGrow: 0,
          flexShrink: 0,
        };
        const otherColumnStyle = {
          width: otherColumnWidth,
          maxWidth: otherColumnWidth,
          flexGrow: 0,
          flexShrink: 0,
        };

        return (
          <View key={item.propriedade.id} style={styles.itemContainer}>
            <Text style={styles.reportTitle}>Demanda de uso de insumos</Text>
            <Text style={styles.propertyName}>
              {item.propriedade.descricao}
            </Text>
            <Text style={styles.period}>
              Período: {startDate} até {endDate}
            </Text>
            <Text style={styles.generatedAt}>
              Relatório gerado em {generatedAt}
            </Text>

            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text
                  style={[
                    styles.colProduto,
                    produtoColumnStyle,
                    styles.tableHeaderText,
                  ]}
                >
                  Produto
                </Text>
                <Text
                  style={[
                    styles.colQtdRecomendada,
                    otherColumnStyle,
                    styles.tableHeaderText,
                  ]}
                >
                  Quantidade{'\n'}Recomendada
                </Text>
                <Text
                  style={[
                    styles.colSaldoFisico,
                    otherColumnStyle,
                    styles.tableHeaderText,
                  ]}
                >
                  Saldo Físico
                </Text>
                <Text
                  style={[
                    styles.colSaldoEmUso,
                    otherColumnStyle,
                    styles.tableHeaderText,
                  ]}
                >
                  Saldo Em Uso
                </Text>
                <Text
                  style={[
                    styles.colSaldo,
                    otherColumnStyle,
                    styles.tableHeaderText,
                  ]}
                >
                  Saldo
                </Text>
                <Text
                  style={[
                    styles.colQtdReceber,
                    otherColumnStyle,
                    styles.tableHeaderText,
                  ]}
                >
                  Quantidade a Receber
                </Text>
              </View>

              {item.insumos.map((insumo) => (
                <View style={styles.tableRow} key={insumo.codigo}>
                  <Text style={[styles.colProduto, produtoColumnStyle]}>
                    {insumo.descricao}
                  </Text>
                  <Text style={[styles.colQtdRecomendada, otherColumnStyle]}>
                    {formatQuantidade(insumo.total, insumo.unidade.abreviatura)}
                  </Text>
                  <Text style={[styles.colSaldoFisico, otherColumnStyle]}>
                    {formatQuantidade(
                      insumo.estoque.saldoFisico,
                      insumo.estoque.unidade.abreviatura,
                    )}
                  </Text>
                  <Text style={[styles.colSaldoEmUso, otherColumnStyle]}>
                    {formatQuantidade(
                      insumo.estoque.saldoEmUso,
                      insumo.estoque.unidade.abreviatura,
                    )}
                  </Text>
                  <Text style={[styles.colSaldo, otherColumnStyle]}>
                    {formatQuantidade(
                      insumo.saldo,
                      insumo.estoque.unidade.abreviatura,
                    )}
                  </Text>
                  <Text style={[styles.colQtdReceber, otherColumnStyle]}>
                    {formatQuantidade(
                      insumo.estoque.saldoReceber,
                      insumo.estoque.unidade.abreviatura,
                    )}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        );
      })}

      <View style={styles.footer} fixed>
        <Text>{pageUrl}</Text>
        <Text
          render={({ pageNumber, totalPages }) => `${pageNumber}/${totalPages}`}
        />
      </View>
    </Page>
  </Document>
);

export default DemandaUsoInsumoReportDoc;
